import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Button, Modal, Form, Input, Upload, message, Space, Tag, Collapse, Row, Col, List, Typography, Image, Select, Popconfirm, DatePicker, Alert, Tabs, Table } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, FileTextOutlined, MedicineBoxOutlined, ExperimentOutlined, PaperClipOutlined, CalendarOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FileImageOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, ScanOutlined, ScissorOutlined, EyeOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ToothChart from '../../components/Dental/ToothChart';
import DentalTab from '../../components/Dental/DentalTab';
import { hasChart, describeTooth } from '../../utils/dental';
import { patientService, uploadService, doctorService, settingsService, glassesOrderService } from '../../services';
import { printGlassesPrescription } from '../../components/Print/printGlassesPrescription';
import {
    EYES, POWER_COLUMNS, EXAM_COLUMNS, hasGlassesSpec, hasEyeExam, hasDental,
    ORDER_STATUS_COLORS, orderStatusLabel
} from '../../utils/glasses';
import { VISIT_FIELD_KEYS, isVisitFieldVisible, defaultVisitFields } from '../../utils/visitFields';
import { testService } from '../../services/testService';
import labTestService from '../../services/labTestService';
import { surgeryService } from '../../services/surgeryService';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PrintPrescription from '../../components/Print/PrintPrescription';
import AttachmentPreview from '../../components/AttachmentPreview';
import VisitFormFields from '../../components/VisitFormFields';
import VisitLabResults from '../../components/VisitLabResults';
import LabTrendModal from '../../components/LabTrendModal';
import { printLabSheet } from '../../components/Print/printLabSheet';
import { toVisitLabTestPayload, fromVisitLabTests } from '../../utils/labFlags';
import { labTestFiles, attachmentsOf } from '../../utils/attachments';
import { isObGynSpecialization } from '../../utils/specialization';
import { lifestyleOptions, lifestyleLabel, lifestyleColor } from '../../utils/lifestyle';
import './PatientDetail.css';

const TEST_STATUS_COLORS = { ordered: 'orange', pending: 'gold', completed: 'green', cancelled: 'default' };

const SURGERY_STATUS_COLORS = {
    pending: 'orange',
    approved: 'blue',
    rejected: 'red',
    scheduled: 'purple',
    completed: 'green',
    cancelled: 'default'
};

// Emergency reads as red wherever it appears, so it is never mistaken for a
// routine booking at a glance.
const SURGERY_URGENCY_COLORS = { emergency: 'red', urgent: 'volcano', elective: 'default' };

// The ultrasound report lines, in report order: [field, translation key, fallback].
const ULTRASOUND_LINES = [
    ['rightKidney', 'rightKidney', 'Right Kidney'],
    ['leftKidney', 'leftKidney', 'Left Kidney'],
    ['bladder', 'bladder', 'Bladder'],
    ['prostate', 'prostate', 'Prostate'],
    ['pvru', 'pvru', 'PVRU']
];

const { Panel } = Collapse;
const { Text, Title } = Typography;

const PatientDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [patient, setPatient] = useState(null);
    const [visits, setVisits] = useState([]);
    // Every test ever ordered for this patient, read straight from the lab record
    // so a result the branch lab filed shows up here even when the order was not
    // tied to a visit.
    const [labTests, setLabTests] = useState([]);
    const [labTestsError, setLabTestsError] = useState(null);
    // Which fields this doctor wants on the visit form (see Settings).
    const [visitFields, setVisitFields] = useState(defaultVisitFields());
    const [drugs, setDrugs] = useState([]);
    // Complaints and diagnoses this doctor has written before, offered on the visit
    // form. Empty is a perfectly good state - a new doctor has written none yet.
    const [visitSuggestions, setVisitSuggestions] = useState({ complaints: [], diagnoses: [] });
    // Visits from the doctor's previous system that have been recognised as this
    // patient's. Empty for everyone until someone attaches them.
    const [legacyRecords, setLegacyRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [visitModalVisible, setVisitModalVisible] = useState(false);
    const [editVisitModalVisible, setEditVisitModalVisible] = useState(false);
    const [editingVisit, setEditingVisit] = useState(null);
    const [editPatientModalVisible, setEditPatientModalVisible] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [fileList, setFileList] = useState([]);
    // Films picked on the visit form but not yet uploaded: a file can only be
    // attached to a visit that exists, so they are held here and sent as soon as
    // the visit is saved.
    const [xrayFileList, setXrayFileList] = useState([]);
    const [ctScanFileList, setCtScanFileList] = useState([]);
    const [ultrasoundFileList, setUltrasoundFileList] = useState([]);
    // The lab-test catalog: what "Add Lab Test" on the visit form offers, each
    // test with the boxes it opens.
    const [labCatalog, setLabCatalog] = useState([]);
    const [labCategories, setLabCategories] = useState([]);
    // Every catalog-driven lab test on this patient's visits, with the sex the
    // server judged the ranges by. Grouped by visit below.
    const [visitLabResults, setVisitLabResults] = useState([]);
    const [resultsSex, setResultsSex] = useState(null);
    // Which field's trend is open, if any: { fieldId, label }.
    const [trend, setTrend] = useState(null);
    const [deletingVisitId, setDeletingVisitId] = useState(null);
    // Operations requested for this patient. Read here rather than only on the
    // Surgery page: a doctor deciding what to do next needs to know the patient
    // is already booked for something, and the profile is where they look.
    const [surgeries, setSurgeries] = useState([]);
    const [surgeryModalVisible, setSurgeryModalVisible] = useState(false);
    const [surgeryForm] = Form.useForm();
    const [form] = Form.useForm();
    const [editVisitForm] = Form.useForm();
    const [editPatientForm] = Form.useForm();
    const { t, i18n } = useTranslation();

    // Check if user is a doctor
    const isDoctor = user?.role === 'doctor';
    // Branch doctors can push a visit's prescriptions to the branch pharmacy.
    const canSendToPharmacy = isDoctor && Boolean(user?.branchId);
    const [sendingVisitId, setSendingVisitId] = useState(null);
    // The glasses orders sent to the optician for this patient, by visit, so
    // each visit shows how far along its glasses are.
    const [glassesOrders, setGlassesOrders] = useState({});
    const [sendingGlassesVisitId, setSendingGlassesVisitId] = useState(null);
    // Which section of the file is open. A link from the instalments list
    // (?tab=dental) opens the file on the section it is about.
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'visits');
    const [activeDentalPlans, setActiveDentalPlans] = useState(0);
    // Obs & Gyn follow-up needs height and BMI on every visit.
    const showObGynVitals = isObGynSpecialization(user);

    // BMI = kg / m². Recalculated whenever weight or height changes so it never
    // has to be worked out by hand; still editable if the doctor wants to override.
    const syncBmi = (formInstance) => (changedValues, allValues) => {
        const changedVitals = changedValues?.vitalSigns;
        if (!changedVitals || (!('weight' in changedVitals) && !('height' in changedVitals))) {
            return;
        }

        const weight = parseFloat(allValues?.vitalSigns?.weight);
        const heightCm = parseFloat(allValues?.vitalSigns?.height);
        if (!weight || !heightCm) {
            formInstance.setFieldValue(['vitalSigns', 'bmi'], undefined);
            return;
        }

        const heightM = heightCm / 100;
        formInstance.setFieldValue(['vitalSigns', 'bmi'], (weight / (heightM * heightM)).toFixed(1));
    };

    // EDD = LMP + 280 days (Naegele's rule). Recounted whenever the LMP changes so
    // the due date never has to be worked out by hand; still editable, because a
    // dating scan overrides the arithmetic. Clearing the LMP clears it too, rather
    // than leaving a due date behind with nothing it was counted from.
    const syncEdd = (formInstance) => (changedValues) => {
        if (!changedValues || !('lmp' in changedValues)) return;

        const lmp = changedValues.lmp ? dayjs(changedValues.lmp) : null;
        formInstance.setFieldValue('edd', lmp && lmp.isValid()
            ? lmp.add(280, 'day').format('YYYY-MM-DD')
            : undefined);
    };

    // Everything the visit form works out for itself while the doctor types.
    const syncDerivedFields = (formInstance) => {
        const handlers = [syncBmi(formInstance), syncEdd(formInstance)];
        return (changedValues, allValues) => handlers.forEach((sync) => sync(changedValues, allValues));
    };

    useEffect(() => {
        fetchPatientData();
        fetchLabTests();
        fetchDrugs();
        fetchVisitSuggestions();
        fetchLegacyRecords();
        fetchVisitFields();
        fetchLabCatalog();
        fetchVisitLabResults();
        fetchSurgeries();
        fetchGlassesOrders();
    }, [id]);

    // Best-effort as well: most patients have no glasses orders at all, and a
    // problem reading them must not cost the doctor the rest of the profile.
    const fetchGlassesOrders = async () => {
        try {
            const response = await glassesOrderService.getForPatient(id);
            setGlassesOrders((response.data || []).reduce(
                (acc, order) => ({ ...acc, [String(order.visitId)]: order }),
                {}
            ));
        } catch (error) {
            console.error('Failed to load glasses orders:', error.message);
            setGlassesOrders({});
        }
    };

    // Best-effort, like the lab tests: a surgery-list problem must not blank out
    // the profile.
    const fetchSurgeries = async () => {
        try {
            const data = await surgeryService.getRequests({ patientId: id });
            setSurgeries(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to load surgeries:', error.message);
            setSurgeries([]);
        }
    };

    // Best-effort, like the drug list: a catalog problem costs the doctor the
    // lab section of the form, not the visit.
    const fetchLabCatalog = async () => {
        try {
            const response = await labTestService.getCatalog();
            setLabCatalog(response.data || []);
            setLabCategories(response.categories || []);
        } catch (error) {
            console.error('Failed to load lab test catalog:', error.message);
            setLabCatalog([]);
        }
    };

    // The catalog-driven results of every visit, read once and grouped by
    // visit. Re-read after any visit save, since that is what writes them.
    const fetchVisitLabResults = async () => {
        try {
            const response = await labTestService.getPatientResults(id);
            setVisitLabResults(response.data || []);
            setResultsSex(response.sex || null);
        } catch (error) {
            console.error('Failed to load lab results:', error.message);
            setVisitLabResults([]);
        }
    };

    // The form layout belongs to the treating doctor: a secretary opening the same
    // page sees the form their doctor configured. Failure leaves every field on -
    // never let a settings hiccup hide the clinical form.
    const fetchVisitFields = async () => {
        const doctorId = isDoctor ? user?._id : user?.doctorId;
        if (!doctorId) return;
        try {
            const data = await settingsService.getSettings(doctorId);
            setVisitFields(VISIT_FIELD_KEYS.reduce((acc, key) => ({
                ...acc,
                [key]: isVisitFieldVisible(data.visitFields, key)
            }), {}));
        } catch (error) {
            console.error('Failed to load visit form settings:', error.message);
            setVisitFields(defaultVisitFields());
        }
    };

    // Passed to the visit form, which decides for itself which sections and
    // headings still have something under them.
    const showField = (key) => visitFields[key] !== false;

    const fetchPatientData = async () => {
        setLoading(true);
        try {
            const [patientRes, visitsRes] = await Promise.all([
                patientService.getPatient(id),
                patientService.getVisits(id)
            ]);
            setPatient(patientRes.data);
            setVisits(visitsRes.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    // Kept out of fetchPatientData: a lab-record problem must not blank out the
    // profile, and the profile must still render for a patient with no tests.
    const fetchLabTests = async () => {
        try {
            const data = await testService.getPatientTests(id);
            setLabTests(Array.isArray(data) ? data : []);
            setLabTestsError(null);
        } catch (error) {
            // Still non-fatal for the rest of the profile, but no longer silent:
            // swallowing this made a failed request look exactly like "this patient
            // has no lab results", which is how a filed result appears to go missing.
            console.error('Failed to load lab tests:', error.message);
            setLabTests([]);
            setLabTestsError(error.message || t('error'));
        }
    };

    // Best-effort: the visit form works perfectly well without suggestions, so a
    // failure here must never stop the doctor writing the visit.
    const fetchVisitSuggestions = async () => {
        try {
            const response = await doctorService.getVisitSuggestions();
            setVisitSuggestions({
                complaints: response.data?.complaints || [],
                diagnoses: response.data?.diagnoses || []
            });
        } catch (error) {
            console.error('Failed to load visit suggestions:', error.message);
        }
    };

    // Best-effort, like the suggestions: a patient with no imported history is the
    // normal case, and a failure here must not take the page down.
    const fetchLegacyRecords = async () => {
        try {
            const response = await doctorService.getLegacyRecords({ patientId: id, pageSize: 100 });
            setLegacyRecords(response.data || []);
        } catch (error) {
            console.error('Failed to load old system records:', error.message);
        }
    };

    const fetchDrugs = async () => {
        try {
            const response = await doctorService.getDrugs();
            setDrugs(response.data);
        } catch (error) {
            console.error('Failed to fetch drugs');
        }
    };
    const handleEditPatient = () => {
        editPatientForm.setFieldsValue({
            name: patient?.name,
            phoneNumber: patient?.phoneNumber,
            email: patient?.email,
            dateOfBirth: patient?.dateOfBirth ? dayjs(patient.dateOfBirth) : null,
            gender: patient?.gender,
            bloodType: patient?.bloodType,
            address: patient?.address,
            allergies: patient?.allergies,
            chronicDiseases: patient?.chronicDiseases,
            notes: patient?.notes
        });
        setEditPatientModalVisible(true);
    };

    const handleUpdatePatient = async (values) => {
        try {
            setSubmitLoading(true);
            const updateData = {
                ...values,
                dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null
            };
            await patientService.updatePatient(id, updateData);
            message.success(t('success'));
            setEditPatientModalVisible(false);
            editPatientForm.resetFields();
            fetchPatientData();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeletePatient = async () => {
        try {
            await patientService.deletePatient(id);
            message.success(t('patientDeletedSuccessfully'));
            navigate(isDoctor ? '/doctor/patients' : '/secretary/patients');
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };
    // Calculate statistics
    const totalVisits = visits.length;
    const totalPrescriptions = visits.reduce((sum, visit) => sum + (visit.prescriptions?.length || 0), 0);
    const lastVisit = visits.length > 0 ? visits[0].visitDate : null;
    const pendingLabTests = labTests.filter((test) => test.status === 'ordered' || test.status === 'pending').length;

    // The catalog-driven results by visit id, and the live fields by id (for
    // translated labels on the read-only tables and the print sheet).
    const resultsByVisit = visitLabResults.reduce((acc, row) => {
        const key = String(row.visitId?._id || row.visitId);
        (acc[key] = acc[key] || []).push(row);
        return acc;
    }, {});
    const fieldById = new Map(
        labCatalog.flatMap((test) => (test.fields || []).map((field) => [String(field._id), field]))
    );

    // The A4 result sheet for one visit, in a print window. The clinic header
    // comes from the same profile the prescription print uses.
    const handlePrintLabSheet = (visit) => {
        let doctorInfo = user || {};
        try {
            const stored = localStorage.getItem('doctorProfile');
            if (stored) doctorInfo = { ...doctorInfo, ...JSON.parse(stored) };
        } catch (error) {
            console.error('Error parsing stored doctor data:', error);
        }
        const opened = printLabSheet({
            visit,
            patient,
            doctorInfo,
            rows: resultsByVisit[visit._id] || [],
            fieldById,
            sex: resultsSex,
            lang: i18n.language,
            t
        });
        if (!opened) message.warning(t('popupBlocked'));
    };
    // Anything not yet done or called off is still hanging over the patient, and
    // is what the tab badge counts.
    const pendingSurgeries = surgeries.filter(
        (surgery) => !['completed', 'cancelled', 'rejected'].includes(surgery.status)
    ).length;

    // Collect all attachments: the ones added on a visit, plus every result sheet
    // the lab uploaded — those belong to the patient's file just the same, and
    // they are the reason this gallery exists.
    const visitAttachments = visits.flatMap(visit =>
        (visit.attachments || []).map(att => ({
            ...att,
            visitDate: visit.visitDate,
            diagnosis: visit.diagnosis
        }))
    );

    const labAttachments = labTests.flatMap((test) =>
        attachmentsOf(test).map((file) => ({
            ...file,
            visitDate: test.resultDate || test.orderDate,
            diagnosis: test.testName
        }))
    );

    const allAttachments = [...visitAttachments, ...labAttachments];
    const totalAttachments = allAttachments.length;

    // Everything ever prescribed to this patient, newest visit first: their
    // medication history, read off the visits rather than kept as its own record.
    const patientDrugs = visits.flatMap((visit) => (visit.prescriptions || [])
        .filter((prescription) => prescription && prescription.drugName)
        .map((prescription, index) => ({
            ...prescription,
            key: `${visit._id}-${index}`,
            visitDate: visit.visitDate,
            diagnosis: visit.diagnosis
        })));

    // Age is what a doctor actually asks for; the date of birth is the thing that
    // happens to be stored. Null rather than 0 when unknown, so an unrecorded
    // birthday does not read as a newborn.
    const patientAge = patient?.dateOfBirth ? dayjs().diff(dayjs(patient.dateOfBirth), 'year') : null;

    // The counts, as one strip. Built as data so the header stays one row of
    // markup no matter how many of these there end up being.
    const headerStats = [
        { label: t('visits') || 'Visits', value: totalVisits },
        { label: t('prescriptions'), value: totalPrescriptions },
        { label: t('labTests'), value: labTests.length },
        { label: t('attachments'), value: totalAttachments },
        {
            label: t('lastVisit') || 'Last visit',
            value: lastVisit ? dayjs(lastVisit).format('DD MMM YYYY') : '-'
        }
    ];

    // Send whatever films are staged to the visit they belong to. Shared by
    // create and edit, which stage them the same way. A film that fails to upload
    // is reported but does not fail the visit: the consultation is written, and
    // the doctor can attach the image again.
    const uploadImagingFiles = async (visitId) => {
        const staged = [
            ['xray', xrayFileList],
            ['ctScan', ctScanFileList],
            ['ultrasound', ultrasoundFileList]
        ];

        for (const [modality, files] of staged) {
            for (const file of files) {
                if (!file.originFileObj) continue;
                try {
                    await uploadService.uploadImaging(visitId, modality, file.originFileObj);
                } catch (error) {
                    message.warning(`${file.name}: ${error.response?.data?.message || error.message}`);
                }
            }
        }
    };

    // The lab cards as orders on the lab queue, tied to the visit, so the lab
    // sees named boxes to fill in. Best-effort after the visit is saved: a
    // queue problem must not cost the doctor the consultation they just wrote.
    const orderLabTestsFromCards = async (visitId, cards = []) => {
        const byId = new Map(labCatalog.map((test) => [String(test._id), test]));
        const tests = (cards || [])
            .map((card) => byId.get(String(card?.labTestId || '')))
            .filter(Boolean)
            .map((test) => (test.libraryCode
                ? { libraryCode: test.libraryCode }
                : { testName: test.name, testType: 'other' }));
        if (tests.length === 0) return;
        try {
            const response = await testService.orderTests({ patientId: id, visitId, tests });
            message.success(`${response?.count || tests.length} ${t('labOrdersSent') || 'test(s) sent to the lab'}`);
            fetchLabTests();
        } catch (error) {
            message.warning(`${t('labOrdersNotSent') || 'Tests were not sent to the lab'}: ${error.message}`);
        }
    };

    // A save that fails validation used to look like a button that does
    // nothing: the offending box was off screen. Say so, and go there.
    const handleVisitFormInvalid = ({ errorFields }) => {
        message.warning(t('fixHighlightedFields') || 'Please fix the highlighted fields');
        console.warn('Visit form validation failed:', errorFields);
    };

    const handleCreateVisit = async (values) => {
        if (submitLoading) return; // Prevent double submission

        setSubmitLoading(true);
        try {
            // First create the visit. The lab cards go with it, in the API's
            // shape: every box of every test, empties as null.
            const response = await patientService.createVisit(id, {
                ...values,
                visitLabTests: toVisitLabTestPayload(values.visitLabTests, labCatalog)
            });
            const newVisitId = response.data._id;

            // Then upload attachments if any
            if (fileList.length > 0) {
                for (const file of fileList) {
                    await uploadService.uploadAttachment(newVisitId, file.originFileObj);
                }
            }

            // The films go to their own modality, so the CT images stay with the CT
            // findings instead of landing in the general attachment pile.
            await uploadImagingFiles(newVisitId);

            if (values.orderFromLab) await orderLabTestsFromCards(newVisitId, values.visitLabTests);

            // Any drug typed into the prescription is now on the doctor's list, and
            // the response carries that list - so the next prescription offers it in
            // the dropdown straight away, with no reload.
            if (Array.isArray(response.drugs)) setDrugs(response.drugs);

            message.success(t('success'));
            // Branch visits go to the pharmacy on save; say so, and say so when they
            // did not, so the doctor knows whether to use "Send to Pharmacy".
            if (response.sentToPharmacy) {
                message.info(t('prescriptionSent') || 'Prescription sent to pharmacy');
            } else if (canSendToPharmacy && (values.prescriptions || []).some((p) => p?.drugName)) {
                message.warning(t('prescriptionNotSent') || 'Prescription could not be sent to the pharmacy — use "Send to Pharmacy" on the visit');
            }
            setVisitModalVisible(false);
            setFileList([]);
            setXrayFileList([]);
            setCtScanFileList([]);
            setUltrasoundFileList([]);
            form.resetFields();
            fetchPatientData();
            fetchVisitLabResults();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleEditVisit = (visit) => {
        setEditingVisit(visit);
        // Films already on the visit are shown from `editingVisit`; these two hold
        // only what is added during this edit.
        setXrayFileList([]);
        setCtScanFileList([]);
        setUltrasoundFileList([]);
        editVisitForm.setFieldsValue({
            visitDate: visit.visitDate ? dayjs(visit.visitDate).format('YYYY-MM-DDTHH:mm') : null,
            complaints: visit.complaints,
            diagnosis: visit.diagnosis,
            gpm: visit.gpm,
            // The date input takes a plain YYYY-MM-DD string, not a dayjs object.
            lmp: visit.lmp ? dayjs(visit.lmp).format('YYYY-MM-DD') : null,
            edd: visit.edd ? dayjs(visit.edd).format('YYYY-MM-DD') : null,
            dngh: visit.dngh,
            psh: visit.psh,
            pmh: visit.pmh,
            onExamination: visit.onExamination,
            xray: visit.xray,
            ctScan: visit.ctScan,
            pelvicUs: visit.pelvicUs,
            obstetricUs: visit.obstetricUs,
            ultrasound: visit.ultrasound || {},
            dental: visit.dental || {},
            eyeExam: visit.eyeExam || {},
            glasses: visit.glasses || {},
            prescriptions: visit.prescriptions || [],
            // The catalog-driven lab cards, from the stored rows; each keeps its
            // id so the server can tell an edit from an addition.
            visitLabTests: fromVisitLabTests(resultsByVisit[visit._id] || []),
            vitalSigns: visit.vitalSigns,
            notes: visit.notes,
            nextVisitDate: visit.nextVisitDate ? dayjs(visit.nextVisitDate).format('YYYY-MM-DDTHH:mm') : null
        });
        setEditVisitModalVisible(true);
    };

    const handleUpdateVisit = async (values) => {
        if (submitLoading || !editingVisit) return;

        setSubmitLoading(true);
        try {
            const response = await patientService.updateVisit(editingVisit._id, {
                ...values,
                visitLabTests: toVisitLabTestPayload(values.visitLabTests, labCatalog)
            });
            // A drug added while editing joins the list just the same.
            if (Array.isArray(response.drugs)) setDrugs(response.drugs);

            await uploadImagingFiles(editingVisit._id);
            if (values.orderFromLab) await orderLabTestsFromCards(editingVisit._id, values.visitLabTests);

            message.success(t('visitUpdatedSuccessfully'));
            setEditVisitModalVisible(false);
            editVisitForm.resetFields();
            setEditingVisit(null);
            setXrayFileList([]);
            setCtScanFileList([]);
            setUltrasoundFileList([]);
            fetchPatientData();
            fetchVisitLabResults();
            // An order the optician has not started follows the edit.
            fetchGlassesOrders();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSubmitLoading(false);
        }
    };

    // Requesting an operation for this patient. The patient is already known -
    // that is the point of doing it here rather than on the Surgery page, where
    // the doctor has to find them in a dropdown first.
    const handleCreateSurgery = async (values) => {
        if (submitLoading) return;

        setSubmitLoading(true);
        try {
            await surgeryService.createRequest({
                ...values,
                patientId: id,
                // A date picker hands back a dayjs object; the API wants a date.
                preferredDate: values.preferredDate ? values.preferredDate.toISOString() : undefined,
                estimatedDuration: values.estimatedDuration ? Number(values.estimatedDuration) : undefined
            });
            message.success(t('surgeryRequestCreated'));
            setSurgeryModalVisible(false);
            surgeryForm.resetFields();
            fetchSurgeries();
        } catch (error) {
            message.error(error.message || t('error'));
        } finally {
            setSubmitLoading(false);
        }
    };

    // Removing a consultation from the record. Doctor-only, confirmed, and
    // reversible on the server - the visit is hidden rather than destroyed, so a
    // misclick costs a support call and not the patient's history.
    const handleDeleteVisit = async (visitId) => {
        setDeletingVisitId(visitId);
        try {
            await patientService.deleteVisit(visitId);
            message.success(t('visitDeleted') || 'Visit deleted');
            fetchPatientData();
            fetchVisitLabResults();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setDeletingVisitId(null);
        }
    };

    // A film already stored on the visit being edited. Removed on the server
    // straight away, and taken off the open form so the doctor sees it go.
    const handleRemoveExistingImaging = async (modality, file) => {
        if (!editingVisit) return;
        try {
            await uploadService.deleteImaging(editingVisit._id, modality, file._id);
            const field = { xray: 'xrayFiles', ctScan: 'ctScanFiles', ultrasound: 'ultrasoundFiles' }[modality];
            setEditingVisit({
                ...editingVisit,
                [field]: (editingVisit[field] || []).filter((existing) => existing._id !== file._id)
            });
            fetchPatientData();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSendToPharmacy = async (visitId) => {
        setSendingVisitId(visitId);
        try {
            await patientService.sendPrescriptionToPharmacy(visitId);
            message.success(t('prescriptionSent') || 'Prescription sent to pharmacy');
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSendingVisitId(null);
        }
    };

    const handleSendToOptician = async (visitId) => {
        setSendingGlassesVisitId(visitId);
        try {
            await glassesOrderService.sendFromVisit(visitId);
            message.success(t('glassesOrderSent'));
            fetchGlassesOrders();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSendingGlassesVisitId(null);
        }
    };

    // The treating doctor's letterhead: the doctor's own, or - for a secretary -
    // the one cached at login.
    const letterhead = (fallback) => {
        if (isDoctor) return user;
        try {
            return JSON.parse(localStorage.getItem('doctorProfile') || 'null') || fallback || {};
        } catch (error) {
            return fallback || {};
        }
    };

    const handlePrintGlasses = (visit) => {
        const opened = printGlassesPrescription({
            spec: visit.glasses || {},
            patient,
            doctorInfo: letterhead(visit.doctorId),
            date: visit.visitDate,
            lang: i18n.language,
            t
        });
        if (!opened) message.warning(t('popupBlocked'));
    };

    const handleFileUpload = async (visitId, file) => {
        try {
            await uploadService.uploadAttachment(visitId, file);
            message.success(t('success'));
            fetchPatientData();
            return false; // Prevent default upload behavior
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
            return false;
        }
    };

    // Helper function to check if file is an image
    const isImageFile = (fileName) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        return imageExtensions.some(ext => fileName?.toLowerCase().endsWith(ext));
    };

    // Helper function to get file icon
    const getFileIcon = (fileName) => {
        const lower = fileName?.toLowerCase() || '';
        if (lower.endsWith('.pdf')) return <FilePdfOutlined style={{ color: '#05668D', fontSize: 24 }} />;
        if (lower.endsWith('.doc') || lower.endsWith('.docx')) return <FileWordOutlined style={{ color: '#028090', fontSize: 24 }} />;
        if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) return <FileExcelOutlined style={{ color: '#02C39A', fontSize: 24 }} />;
        if (isImageFile(fileName)) return <FileImageOutlined style={{ color: '#00A896', fontSize: 24 }} />;
        return <FileTextOutlined style={{ color: '#05668D', fontSize: 24 }} />;
    };

    return (
        <div>
            {/* One header, in place of a button row, four gradient tiles and a
                twenty-row details table that between them filled the screen before
                a single visit was visible.

                Ordered by what a doctor needs first: who this is, then what would
                change today's prescription (allergies, chronic disease), then the
                counts, and only then the rest - which is folded away, because it is
                reference material rather than something read at every visit. */}
            <Card className="patient-header" styles={{ body: { padding: '12px 16px' } }}>
                <Row gutter={[12, 12]} align="middle" wrap>
                    <Col flex="none">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(isDoctor ? '/doctor/patients' : '/secretary/patients')}
                        />
                    </Col>
                    <Col flex="auto" style={{ minWidth: 200 }}>
                        <Title level={4} style={{ margin: 0 }}>{patient?.name}</Title>
                        <Space size={6} wrap style={{ marginTop: 2 }}>
                            {patientAge !== null && (
                                <Text type="secondary">{patientAge} {t('yearsOld') || 'y'}</Text>
                            )}
                            {patient?.gender && <Text type="secondary">{t(patient.gender)}</Text>}
                            {patient?.bloodType && <Tag color="red">{patient.bloodType}</Tag>}
                            {patient?.phoneNumber && <Text copyable>{patient.phoneNumber}</Text>}
                        </Space>
                    </Col>
                    <Col flex="none">
                        <Space wrap>
                            {isDoctor && (
                                <Button icon={<EditOutlined />} onClick={handleEditPatient}>
                                    {t('edit')}
                                </Button>
                            )}
                            {isDoctor && (
                                <Popconfirm
                                    title={t('confirmDelete')}
                                    description={t('deletePatientConfirmation')}
                                    onConfirm={handleDeletePatient}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button danger icon={<DeleteOutlined />}>{t('delete')}</Button>
                                </Popconfirm>
                            )}
                            {isDoctor && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setVisitModalVisible(true)}
                                >
                                    {t('createVisit')}
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>

                {/* The two things that change what gets prescribed today. Given a
                    band of their own so they cannot be missed, and shown only when
                    there is something to say. */}
                {(patient?.allergies?.length > 0 || patient?.chronicDiseases?.length > 0) && (
                    <div className="patient-alerts">
                        {patient?.allergies?.length > 0 && (
                            <div className="patient-alert patient-alert-allergy">
                                <Text strong>{t('allergies')}:</Text>
                                <Space size={4} wrap>
                                    {patient.allergies.map((item) => (
                                        <Tag color="orange" key={item}>{item}</Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                        {patient?.chronicDiseases?.length > 0 && (
                            <div className="patient-alert">
                                <Text strong>{t('chronicDiseases')}:</Text>
                                <Space size={4} wrap>
                                    {patient.chronicDiseases.map((item) => (
                                        <Tag color="red" key={item}>{item}</Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </div>
                )}

                <div className="patient-stats">
                    {headerStats.map((stat) => (
                        <div className="patient-stat" key={stat.label}>
                            <span className="patient-stat-value">{stat.value}</span>
                            <span className="patient-stat-label">{stat.label}</span>
                        </div>
                    ))}
                </div>

                <Collapse
                    ghost
                    size="small"
                    className="patient-more"
                    // Open on arrival: this is the patient's file, and having to
                    // click to see their date of birth or address was a step back
                    // from the table it replaced. It still folds away for a doctor
                    // who wants the visits higher up the screen.
                    defaultActiveKey={['details']}
                    items={[{
                        key: 'details',
                        label: <Text type="secondary">{t('patientDetails')}</Text>,
                        children: (
                            <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small" colon={false}>
                                <Descriptions.Item label={t('dateOfBirth')}>
                                    {patient?.dateOfBirth ? dayjs(patient.dateOfBirth).format('YYYY-MM-DD') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('email')}>{patient?.email || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('occupation')}>{patient?.occupation || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('lifestyle')}>
                                    <Space size={4} wrap>
                                        <Tag color={lifestyleColor(patient?.smoker)}>
                                            {t('smoker')}: {lifestyleLabel(t, patient?.smoker)}
                                        </Tag>
                                        <Tag color={lifestyleColor(patient?.alcoholic)}>
                                            {t('alcoholic')}: {lifestyleLabel(t, patient?.alcoholic)}
                                        </Tag>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('address')} span={2}>
                                    {patient?.address || '-'}
                                </Descriptions.Item>
                                {patient?.pastSurgicalHistory && (
                                    <Descriptions.Item label={t('pastSurgicalHistory')} span={3}>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{patient.pastSurgicalHistory}</span>
                                    </Descriptions.Item>
                                )}
                                {patient?.familyHistoryFather && (
                                    <Descriptions.Item label={t('familyHistoryFather')} span={3}>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{patient.familyHistoryFather}</span>
                                    </Descriptions.Item>
                                )}
                                {patient?.familyHistoryMother && (
                                    <Descriptions.Item label={t('familyHistoryMother')} span={3}>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{patient.familyHistoryMother}</span>
                                    </Descriptions.Item>
                                )}
                                {patient?.notes && (
                                    <Descriptions.Item label={t('notes')} span={3}>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{patient.notes}</span>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        )
                    }]}
                />
            </Card>

            {/* One section at a time. These four used to be stacked cards: to
                reach a lab result the doctor scrolled past every visit, and to
                reach a visit they scrolled past every attachment. Visits open
                first because that is what the page is usually opened for. */}
            <Tabs
                className="patient-sections"
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'visits',
                        label: `${t('visitHistory')} (${visits.length})`,
                        children: (
                            <>
            {/* Visit History */}
            <Card
                title={
                    <Space>
                        <CalendarOutlined />
                        {t('visitHistory')} ({visits.length})
                    </Space>
                }
            >
                {visits.length === 0 ? (
                    <Text type="secondary">{t('noData')}</Text>
                ) : (
                    <Collapse accordion>
                        {visits.map((visit, index) => (
                            <Panel
                                header={
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Space>
                                            <Tag color="blue">Visit {visits.length - index}</Tag>
                                            {/* A visit imported from the old system usually has no date -
                                                the old records did not carry one - so say that rather than
                                                render "Invalid Date". */}
                                            <Text strong>
                                                {visit.visitDate
                                                    ? dayjs(visit.visitDate).format('YYYY-MM-DD HH:mm')
                                                    : t('dateUnknown')}
                                            </Text>
                                            {visit.legacyVisitId && (
                                                <Tag color="gold">{t('fromOldSystem')} #{visit.legacyVisitId}</Tag>
                                            )}
                                            {visit.diagnosis && <Text>- {visit.diagnosis}</Text>}
                                        </Space>
                                        <Space>
                                            {isDoctor && (
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditVisit(visit);
                                                    }}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            )}
                                            {isDoctor && (
                                                // Wrapped so the click that opens the
                                                // confirmation does not also toggle the
                                                // panel underneath it.
                                                <span onClick={(e) => e.stopPropagation()}>
                                                    <Popconfirm
                                                        title={t('deleteVisit') || 'Delete visit'}
                                                        description={
                                                            t('deleteVisitConfirmation') ||
                                                            'Remove this visit from the history? Its prescriptions go with it. Lab results stay on the patient record.'
                                                        }
                                                        onConfirm={() => handleDeleteVisit(visit._id)}
                                                        okText={t('yes')}
                                                        cancelText={t('no')}
                                                        okButtonProps={{ danger: true }}
                                                    >
                                                        <Button
                                                            danger
                                                            size="small"
                                                            icon={<DeleteOutlined />}
                                                            loading={deletingVisitId === visit._id}
                                                        >
                                                            {t('delete')}
                                                        </Button>
                                                    </Popconfirm>
                                                </span>
                                            )}
                                            {canSendToPharmacy && visit.prescriptions?.length > 0 && (
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    icon={<MedicineBoxOutlined />}
                                                    loading={sendingVisitId === visit._id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSendToPharmacy(visit._id);
                                                    }}
                                                >
                                                    {t('sendToPharmacy') || 'Send to Pharmacy'}
                                                </Button>
                                            )}
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<PrinterOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedVisit({ ...visit });
                                                }}
                                                style={{
                                                    background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                                                    border: 'none'
                                                }}
                                            >
                                                Print
                                            </Button>
                                        </Space>
                                    </Space>
                                }
                                key={visit._id}
                            >
                                <Descriptions bordered column={1} size="small">
                                    <Descriptions.Item label={t('complaints')}>
                                        {visit.complaints || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('diagnosis')}>
                                        {visit.diagnosis || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('vitalSigns')}>
                                        <Space wrap>
                                            {visit.vitalSigns?.bloodPressure && (
                                                <Tag>BP: {visit.vitalSigns.bloodPressure}</Tag>
                                            )}
                                            {visit.vitalSigns?.temperature && (
                                                <Tag>Temp: {visit.vitalSigns.temperature}°C</Tag>
                                            )}
                                            {visit.vitalSigns?.pulse && (
                                                <Tag>Pulse: {visit.vitalSigns.pulse} bpm</Tag>
                                            )}
                                            {visit.vitalSigns?.weight && (
                                                <Tag>Weight: {visit.vitalSigns.weight} kg</Tag>
                                            )}
                                            {visit.vitalSigns?.height && (
                                                <Tag>Height: {visit.vitalSigns.height} cm</Tag>
                                            )}
                                            {visit.vitalSigns?.bmi && (
                                                <Tag color="purple">BMI: {visit.vitalSigns.bmi}</Tag>
                                            )}
                                        </Space>
                                    </Descriptions.Item>
                                    {/* Background as recorded at this visit. Hidden when
                                        nothing was filled in, so older visits stay compact. */}
                                    {(visit.gpm || visit.lmp || visit.edd || visit.dngh || visit.psh || visit.pmh) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <FileTextOutlined />
                                                {t('patientHistory') || 'Patient History'}
                                            </Space>
                                        }>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                {visit.gpm && (
                                                    <Text><Text strong>{t('gpm')}:</Text> {visit.gpm}</Text>
                                                )}
                                                {visit.lmp && (
                                                    <Text>
                                                        <Text strong>{t('lmp')}:</Text> {dayjs(visit.lmp).format('YYYY-MM-DD')}
                                                    </Text>
                                                )}
                                                {visit.edd && (
                                                    <Text>
                                                        <Text strong>{t('edd')}:</Text> {dayjs(visit.edd).format('YYYY-MM-DD')}
                                                    </Text>
                                                )}
                                                {visit.dngh && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('dngh')}:</Text> {visit.dngh}
                                                    </Text>
                                                )}
                                                {visit.psh && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('psh')}:</Text> {visit.psh}
                                                    </Text>
                                                )}
                                                {visit.pmh && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('pmh')}:</Text> {visit.pmh}
                                                    </Text>
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {visit.onExamination && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <FileTextOutlined />
                                                {t('onExamination') || 'On Examination'}
                                            </Space>
                                        }>
                                            <Text style={{ whiteSpace: 'pre-wrap' }}>{visit.onExamination}</Text>
                                        </Descriptions.Item>
                                    )}
                                    {visit.prescriptions && visit.prescriptions.length > 0 && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <MedicineBoxOutlined />
                                                {t('prescriptions')}
                                            </Space>
                                        }>
                                            <List
                                                size="small"
                                                dataSource={visit.prescriptions}
                                                renderItem={(p, i) => {
                                                    // Dosage, frequency and duration are optional, so only the
                                                    // ones actually filled in are listed — no empty labels.
                                                    const details = [
                                                        p.dosage && `${t('dosage')}: ${p.dosage}`,
                                                        p.frequency && `${t('frequency')}: ${p.frequency}`,
                                                        p.duration && `${t('duration')}: ${p.duration}`
                                                    ].filter(Boolean);
                                                    return (
                                                        <List.Item>
                                                            <Space direction="vertical" size={0}>
                                                                <Text strong>{i + 1}. {p.drugName}</Text>
                                                                {details.length > 0 && (
                                                                    <Text type="secondary">{details.join(' | ')}</Text>
                                                                )}
                                                                {p.notes && <Text type="secondary">Notes: {p.notes}</Text>}
                                                            </Space>
                                                        </List.Item>
                                                    );
                                                }}
                                            />
                                        </Descriptions.Item>
                                    )}
                                    {(visit.xray || visit.ctScan || visit.pelvicUs || visit.obstetricUs
                                        || visit.xrayFiles?.length > 0 || visit.ctScanFiles?.length > 0) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <ScanOutlined />
                                                {t('imaging')}
                                            </Space>
                                        }>
                                            {/* Findings and films together, per modality: the
                                                report and the image it describes are read as
                                                one thing. */}
                                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                                {(visit.xray || visit.xrayFiles?.length > 0) && (
                                                    <div>
                                                        {visit.xray && (
                                                            <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                                <Text strong>{t('xray')}:</Text> {visit.xray}
                                                            </Text>
                                                        )}
                                                        {visit.xrayFiles?.length > 0 && (
                                                            <div style={{ marginTop: 4 }}>
                                                                {!visit.xray && <Text strong>{t('xray')}: </Text>}
                                                                <AttachmentPreview files={visit.xrayFiles} size={56} />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {(visit.ctScan || visit.ctScanFiles?.length > 0) && (
                                                    <div>
                                                        {visit.ctScan && (
                                                            <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                                <Text strong>{t('ctScan')}:</Text> {visit.ctScan}
                                                            </Text>
                                                        )}
                                                        {visit.ctScanFiles?.length > 0 && (
                                                            <div style={{ marginTop: 4 }}>
                                                                {!visit.ctScan && <Text strong>{t('ctScan')}: </Text>}
                                                                <AttachmentPreview files={visit.ctScanFiles} size={56} />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {visit.pelvicUs && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('pelvicUs') || 'Pelvic US'}:</Text> {visit.pelvicUs}
                                                    </Text>
                                                )}
                                                {visit.obstetricUs && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('obstetricUs') || 'Obstetric US'}:</Text> {visit.obstetricUs}
                                                    </Text>
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {(ULTRASOUND_LINES.some(([key]) => visit.ultrasound?.[key])
                                        || visit.ultrasound?.note || visit.ultrasoundFiles?.length > 0) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <ScanOutlined />
                                                {t('ultrasound') || 'Ultrasound'}
                                            </Space>
                                        }>
                                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                <Space wrap size={[16, 4]}>
                                                    {ULTRASOUND_LINES.filter(([key]) => visit.ultrasound?.[key]).map(([key, labelKey, fallback]) => (
                                                        <Text key={key}>
                                                            <Text strong>{t(labelKey) || fallback}:</Text> {visit.ultrasound[key]}
                                                        </Text>
                                                    ))}
                                                </Space>
                                                {visit.ultrasound?.note && (
                                                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{visit.ultrasound.note}</Text>
                                                )}
                                                {visit.ultrasoundFiles?.length > 0 && (
                                                    <AttachmentPreview files={visit.ultrasoundFiles} size={56} />
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {hasDental(visit.dental) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <FileTextOutlined />
                                                {t('dental')}
                                            </Space>
                                        }>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                {[
                                                    ['teeth', 'dentalTeeth'],
                                                    ['findings', 'dentalFindings'],
                                                    ['procedure', 'dentalProcedure'],
                                                    ['treatmentPlan', 'dentalTreatmentPlan']
                                                ].filter(([key]) => visit.dental[key]).map(([key, labelKey]) => (
                                                    <Text key={key} style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t(labelKey)}:</Text> {visit.dental[key]}
                                                    </Text>
                                                ))}
                                                {/* The chart as it was marked at this visit;
                                                    the mouth as it stands today is on the
                                                    Dental tab. */}
                                                {hasChart(visit.dental) && (
                                                    <>
                                                        <ToothChart value={visit.dental.chart} readOnly compact />
                                                        {visit.dental.chart.map((entry) => (
                                                            <Text key={entry.tooth}>{describeTooth(t, entry)}</Text>
                                                        ))}
                                                    </>
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {hasEyeExam(visit.eyeExam) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <EyeOutlined />
                                                {t('eyeExam')}
                                            </Space>
                                        }>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                {EYES.filter(([eye]) => EXAM_COLUMNS.some(([suffix]) => visit.eyeExam[`${eye}${suffix}`])).map(([eye, labelKey]) => (
                                                    <Space wrap size={[12, 2]} key={eye}>
                                                        <Text strong>{t(labelKey)}:</Text>
                                                        {EXAM_COLUMNS.filter(([suffix]) => visit.eyeExam[`${eye}${suffix}`]).map(([suffix, columnKey]) => (
                                                            <Text key={suffix}>
                                                                <Text type="secondary">{t(columnKey)}</Text> {visit.eyeExam[`${eye}${suffix}`]}
                                                            </Text>
                                                        ))}
                                                    </Space>
                                                ))}
                                                {visit.eyeExam.anteriorSegment && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('anteriorSegment')}:</Text> {visit.eyeExam.anteriorSegment}
                                                    </Text>
                                                )}
                                                {visit.eyeExam.fundus && (
                                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('fundus')}:</Text> {visit.eyeExam.fundus}
                                                    </Text>
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {(hasGlassesSpec(visit.glasses) || visit.glasses?.note || visit.glasses?.lensType) && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <EyeOutlined />
                                                {t('glassesPrescription')}
                                            </Space>
                                        }>
                                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                {EYES.map(([eye, labelKey]) => (
                                                    <Space wrap size={[12, 2]} key={eye}>
                                                        <Text strong>{t(labelKey)}:</Text>
                                                        {POWER_COLUMNS.filter(([suffix]) => visit.glasses[`${eye}${suffix}`]).map(([suffix, label]) => (
                                                            <Text key={suffix}>
                                                                <Text type="secondary">{label}</Text> <span dir="ltr">{visit.glasses[`${eye}${suffix}`]}</span>
                                                            </Text>
                                                        ))}
                                                    </Space>
                                                ))}
                                                <Space wrap>
                                                    {visit.glasses.pd && <Tag>{t('pupillaryDistance')}: {visit.glasses.pd}</Tag>}
                                                    {visit.glasses.lensType && <Tag color="blue">{visit.glasses.lensType}</Tag>}
                                                </Space>
                                                {visit.glasses.note && (
                                                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{visit.glasses.note}</Text>
                                                )}
                                                {hasGlassesSpec(visit.glasses) && (
                                                    <Space wrap>
                                                        {glassesOrders[visit._id] && (
                                                            <Tag color={ORDER_STATUS_COLORS[glassesOrders[visit._id].status]}>
                                                                {t('optician')}: {orderStatusLabel(t, glassesOrders[visit._id].status)}
                                                            </Tag>
                                                        )}
                                                        {/* Until the optician has started, a second send
                                                            just refreshes the order; after that the
                                                            glasses are being made and it is refused. */}
                                                        {isDoctor && (!glassesOrders[visit._id]
                                                            || ['new', 'cancelled'].includes(glassesOrders[visit._id].status)) && (
                                                            <Button
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                loading={sendingGlassesVisitId === visit._id}
                                                                onClick={() => handleSendToOptician(visit._id)}
                                                            >
                                                                {t('sendToOptician')}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            icon={<PrinterOutlined />}
                                                            onClick={() => handlePrintGlasses(visit)}
                                                        >
                                                            {t('printGlassesPrescription')}
                                                        </Button>
                                                    </Space>
                                                )}
                                                {glassesOrders[visit._id]?.opticianNote && (
                                                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                                        <Text strong>{t('opticianNote')}:</Text> {glassesOrders[visit._id].opticianNote}
                                                    </Text>
                                                )}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {visit.labTests && visit.labTests.length > 0 && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <ExperimentOutlined />
                                                {t('labTests')}
                                            </Space>
                                        }>
                                            <List
                                                size="small"
                                                dataSource={visit.labTests}
                                                renderItem={(test) => {
                                                    const files = labTestFiles(test);
                                                    const filledComponents = (test.components || [])
                                                        .filter((component) => String(component.value || '').trim());
                                                    return (
                                                        <List.Item>
                                                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                                                <Text strong>{test.testName}</Text>
                                                                {/* A panel reads as its own analytes. The
                                                                    summary line below is skipped when they
                                                                    are there - "CBC: -" under fourteen
                                                                    filled-in values says nothing. */}
                                                                {filledComponents.length > 0 ? (
                                                                    <div className="visit-component-results">
                                                                        {filledComponents.map((component) => (
                                                                            <span
                                                                                key={component.code || component.name}
                                                                                className={component.isAbnormal
                                                                                    ? 'visit-component visit-component-abnormal'
                                                                                    : 'visit-component'}
                                                                                title={component.referenceRange
                                                                                    ? `${t('referenceRange')}: ${component.referenceRange}`
                                                                                    : undefined}
                                                                            >
                                                                                <span className="visit-component-name">{component.name}</span>
                                                                                <span className="visit-component-value">
                                                                                    {component.value}{component.unit ? ` ${component.unit}` : ''}
                                                                                </span>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <Text>
                                                                        {t('result')}: {test.result ||
                                                                            (files.length > 0
                                                                                ? (t('seeAttachment') || 'See attachment')
                                                                                : '-')}
                                                                        {test.isAbnormal && (
                                                                            <Tag color="red" style={{ marginInlineStart: 8 }}>
                                                                                {t('abnormal')}
                                                                            </Tag>
                                                                        )}
                                                                    </Text>
                                                                )}
                                                                {test.referenceRange && filledComponents.length === 0 && (
                                                                    <Text type="secondary">
                                                                        {t('referenceRange')}: {test.referenceRange}
                                                                    </Text>
                                                                )}
                                                                {test.result && filledComponents.length > 0 && (
                                                                    <Text type="secondary">{test.result}</Text>
                                                                )}
                                                                {test.date && (
                                                                    <Text type="secondary">
                                                                        {t('date')}: {dayjs(test.date).format('YYYY-MM-DD HH:mm')}
                                                                    </Text>
                                                                )}
                                                                {/* The lab's own result sheet, shown as-is. */}
                                                                {files.length > 0 && (
                                                                    <div style={{ marginTop: 8 }}>
                                                                        <AttachmentPreview files={files} size={120} />
                                                                    </div>
                                                                )}
                                                            </Space>
                                                        </List.Item>
                                                    );
                                                }}
                                            />
                                        </Descriptions.Item>
                                    )}
                                    {(resultsByVisit[visit._id] || []).length > 0 && (
                                        <Descriptions.Item label={
                                            <Space>
                                                <ExperimentOutlined />
                                                {t('labResults') || 'Lab results'}
                                            </Space>
                                        }>
                                            {/* Every value with its unit, the range it was
                                                judged by and its flag. A value opens its trend
                                                across the patient's visits. */}
                                            <VisitLabResults
                                                rows={resultsByVisit[visit._id]}
                                                fieldById={fieldById}
                                                sex={resultsSex}
                                                onTrend={(fieldId, label) => setTrend({ fieldId, label })}
                                                onPrint={() => handlePrintLabSheet(visit)}
                                            />
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label={
                                        <Space>
                                            <PaperClipOutlined />
                                            {t('attachments')}
                                        </Space>
                                    }>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {visit.attachments && visit.attachments.length > 0 && (
                                                <List
                                                    size="small"
                                                    dataSource={visit.attachments}
                                                    renderItem={(att) => (
                                                        <List.Item>
                                                            <a
                                                                href={att.fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <PaperClipOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                                {att.fileName}
                                                            </a>
                                                        </List.Item>
                                                    )}
                                                />
                                            )}
                                            <Upload
                                                beforeUpload={(file) => handleFileUpload(visit._id, file)}
                                                showUploadList={false}
                                            >
                                                <Button icon={<UploadOutlined />} size="small">
                                                    {t('uploadAttachment')}
                                                </Button>
                                            </Upload>
                                        </Space>
                                    </Descriptions.Item>
                                    {visit.notes && (
                                        <Descriptions.Item label={t('notes')}>
                                            {visit.notes}
                                        </Descriptions.Item>
                                    )}
                                    {visit.nextVisitDate && (
                                        <Descriptions.Item label={t('nextVisitDate')}>
                                            {dayjs(visit.nextVisitDate).format('YYYY-MM-DD HH:mm')}
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label={t('createdBy')}>
                                        <Space>
                                            <Text>{visit.createdBy?.name}</Text>
                                            <Tag color={visit.createdBy?.role === 'doctor' ? 'blue' : 'green'}>
                                                {visit.createdBy?.role}
                                            </Tag>
                                        </Space>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Panel>
                        ))}
                    </Collapse>
                )}
            </Card>
                            </>
                        )
                    },
                    // The dental side of the file, for a dentist's patients: the
                    // chart, the treatment plans and the instalments.
                    ...(showField('dental') ? [{
                        key: 'dental',
                        label: (
                            <Space size={6}>
                                {t('dental')}
                                {activeDentalPlans > 0 && <Tag color="blue">{activeDentalPlans}</Tag>}
                            </Space>
                        ),
                        // Rendered even while hidden, so the label's count is
                        // there before the tab is opened.
                        forceRender: true,
                        children: (
                            <DentalTab
                                patientId={id}
                                patient={patient}
                                canEdit={isDoctor}
                                doctorInfo={letterhead()}
                                onCount={setActiveDentalPlans}
                            />
                        )
                    }] : []),
                    {
                        key: 'labs',
                        label: (
                            <Space size={6}>
                                {t('labTests')} ({labTests.length})
                                {/* A test the lab has not filed yet is the one thing
                                    on this tab worth interrupting for. */}
                                {pendingLabTests > 0 && <Tag color="orange">{pendingLabTests}</Tag>}
                            </Space>
                        ),
                        children: (
                            <>
            {/* Lab tests ordered for this patient, with whatever the lab filed back.
                Read from the test record itself, so a result uploaded by the branch
                lab is on the profile immediately — it does not depend on the order
                having been tied to a visit. */}
            {labTestsError && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('labTests')}
                    description={labTestsError}
                    action={
                        <Button size="small" onClick={fetchLabTests}>
                            {t('retry') || 'Retry'}
                        </Button>
                    }
                />
            )}

            {labTests.length > 0 && (
                <Card
                    title={
                        <Space>
                            <ExperimentOutlined />
                            {t('labTests')} ({labTests.length})
                            {pendingLabTests > 0 && (
                                <Tag color="orange">
                                    {pendingLabTests} {t('pending') || 'pending'}
                                </Tag>
                            )}
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <List
                        itemLayout="vertical"
                        dataSource={labTests}
                        rowKey="_id"
                        pagination={labTests.length > 5 ? { pageSize: 5 } : false}
                        renderItem={(test) => {
                            const files = attachmentsOf(test);
                            return (
                                <List.Item key={test._id}>
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <Space wrap>
                                            <Text strong>{test.testName}</Text>
                                            {test.testType && <Tag>{t(test.testType) || test.testType}</Tag>}
                                            <Tag color={TEST_STATUS_COLORS[test.status] || 'default'}>
                                                {t(test.status) || test.status}
                                            </Tag>
                                            {test.isAbnormal && <Tag color="red">{t('abnormal')}</Tag>}
                                        </Space>

                                        <Text>
                                            {t('result')}: {test.resultValue ||
                                                (files.length > 0
                                                    ? (t('seeAttachment') || 'See attachment')
                                                    : '-')}
                                        </Text>

                                        {test.referenceRange && (
                                            <Text type="secondary">
                                                {t('referenceRange')}: {test.referenceRange}
                                            </Text>
                                        )}

                                        <Space wrap size={16}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {t('orderDate')}: {dayjs(test.orderDate).format('YYYY-MM-DD')}
                                            </Text>
                                            {test.resultDate && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {t('date')}: {dayjs(test.resultDate).format('YYYY-MM-DD HH:mm')}
                                                </Text>
                                            )}
                                            {test.doctor?.name && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {t('doctor')}: {test.doctor.name}
                                                </Text>
                                            )}
                                            {(test.labUserId?.name || test.labName) && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {t('lab')}: {test.labUserId?.name || test.labName}
                                                </Text>
                                            )}
                                        </Space>

                                        {test.notes && (
                                            <Text type="secondary">{t('notes')}: {test.notes}</Text>
                                        )}

                                        {/* The lab's own result sheet / photo, shown as it was uploaded. */}
                                        {files.length > 0 && (
                                            <div style={{ marginTop: 4 }}>
                                                <AttachmentPreview files={files} size={120} />
                                            </div>
                                        )}
                                    </Space>
                                </List.Item>
                            );
                        }}
                    />
                </Card>
            )}
                                {labTests.length === 0 && !labTestsError && (
                                    <Card><Text type="secondary">{t('noData')}</Text></Card>
                                )}
                            </>
                        )
                    },
                    {
                        key: 'surgery',
                        label: (
                            <Space size={6}>
                                {t('surgery')} ({surgeries.length})
                                {pendingSurgeries > 0 && <Tag color="orange">{pendingSurgeries}</Tag>}
                            </Space>
                        ),
                        children: (
                            <Card
                                title={<Space><ScissorOutlined />{t('surgeries') || t('surgery')}</Space>}
                                extra={isDoctor && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            surgeryForm.resetFields();
                                            surgeryForm.setFieldsValue({
                                                urgencyLevel: 'elective',
                                                // The working diagnosis is almost always
                                                // the one from the last visit; offered
                                                // filled in, and editable.
                                                diagnosis: visits[0]?.diagnosis
                                            });
                                            setSurgeryModalVisible(true);
                                        }}
                                    >
                                        {t('addSurgery')}
                                    </Button>
                                )}
                            >
                                {surgeries.length === 0 ? (
                                    <Text type="secondary">{t('noData')}</Text>
                                ) : (
                                    <List
                                        dataSource={surgeries}
                                        rowKey="_id"
                                        renderItem={(surgery) => (
                                            <List.Item key={surgery._id}>
                                                <List.Item.Meta
                                                    avatar={<ScissorOutlined style={{ fontSize: 20, color: '#05668D' }} />}
                                                    title={
                                                        <Space wrap>
                                                            <Text strong>{surgery.surgeryType}</Text>
                                                            <Tag color={SURGERY_STATUS_COLORS[surgery.status] || 'default'}>
                                                                {t(surgery.status) || surgery.status}
                                                            </Tag>
                                                            <Tag color={SURGERY_URGENCY_COLORS[surgery.urgencyLevel] || 'default'}>
                                                                {t(surgery.urgencyLevel) || surgery.urgencyLevel}
                                                            </Tag>
                                                        </Space>
                                                    }
                                                    description={
                                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                            {surgery.diagnosis && (
                                                                <Text>{t('diagnosis')}: {surgery.diagnosis}</Text>
                                                            )}
                                                            <Space size={12} wrap>
                                                                {surgery.preferredDate && (
                                                                    <Text type="secondary">
                                                                        {t('preferredDate')}: {dayjs(surgery.preferredDate).format('YYYY-MM-DD')}
                                                                    </Text>
                                                                )}
                                                                {surgery.estimatedDuration && (
                                                                    <Text type="secondary">
                                                                        {t('estimatedDuration')}: {surgery.estimatedDuration} {t('minutes') || 'min'}
                                                                    </Text>
                                                                )}
                                                                {surgery.anesthesiaType && (
                                                                    <Text type="secondary">
                                                                        {t('anesthesiaType')}: {surgery.anesthesiaType}
                                                                    </Text>
                                                                )}
                                                                <Text type="secondary">
                                                                    {t('requested') || 'Requested'}: {dayjs(surgery.createdAt).format('YYYY-MM-DD')}
                                                                </Text>
                                                            </Space>
                                                            {surgery.specialRequirements && (
                                                                <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                                                    {surgery.specialRequirements}
                                                                </Text>
                                                            )}
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </Card>
                        )
                    },
                    {
                        key: 'files',
                        label: `${t('attachments')} (${totalAttachments})`,
                        children: (
                            <>
            {/* All Attachments Section */}
            {
                allAttachments.length > 0 && (
                    <Card
                        title={
                            <Space>
                                <PaperClipOutlined />
                                {t('attachments')} ({allAttachments.length})
                            </Space>
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <Row gutter={[16, 16]}>
                            {allAttachments.map((item, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                    <Card
                                        hoverable
                                        cover={
                                            isImageFile(item.fileName) ? (
                                                <Image
                                                    src={item.fileUrl}
                                                    alt={item.fileName}
                                                    style={{ height: 200, objectFit: 'cover' }}
                                                    preview={{
                                                        mask: 'Click to view'
                                                    }}
                                                    fallback="data:image/png;base64,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"
                                                    crossOrigin="anonymous"
                                                />
                                            ) : (
                                                <div style={{
                                                    height: 200,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#f5f5f5'
                                                }}>
                                                    {getFileIcon(item.fileName)}
                                                </div>
                                            )
                                        }
                                    >
                                        <Card.Meta
                                            title={
                                                <div style={{ fontSize: 12 }}>
                                                    {item.fileName?.length > 25
                                                        ? item.fileName.substring(0, 25) + '...'
                                                        : item.fileName
                                                    }
                                                </div>
                                            }
                                            description={
                                                <Space direction="vertical" size={2}>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        {dayjs(item.visitDate).format('MMM DD, YYYY')}
                                                    </Text>
                                                    {item.diagnosis && (
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            {item.diagnosis.substring(0, 30)}
                                                        </Text>
                                                    )}
                                                    <a
                                                        href={item.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ fontSize: 11 }}
                                                    >
                                                        <FileTextOutlined /> Download
                                                    </a>
                                                </Space>
                                            }
                                        />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                )
            }
                                {allAttachments.length === 0 && (
                                    <Card><Text type="secondary">{t('noData')}</Text></Card>
                                )}
                            </>
                        )
                    },
                    {
                        key: 'drugs',
                        label: `${t('drugs')} (${patientDrugs.length})`,
                        children: (
                            <Card title={<Space><MedicineBoxOutlined />{t('drugs')} ({patientDrugs.length})</Space>}>
                                {/* Only what was written for THIS patient - the doctor's
                                    full drug list lives on its own page. */}
                                {patientDrugs.length === 0 ? (
                                    <Text type="secondary">{t('noData')}</Text>
                                ) : (
                                    <Table
                                        size="small"
                                        dataSource={patientDrugs}
                                        pagination={patientDrugs.length > 10 ? { pageSize: 10 } : false}
                                        scroll={{ x: true }}
                                        columns={[
                                            {
                                                title: t('date'),
                                                dataIndex: 'visitDate',
                                                width: 110,
                                                render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : t('dateUnknown'))
                                            },
                                            { title: t('drugName'), dataIndex: 'drugName', render: (value) => <Text strong>{value}</Text> },
                                            { title: t('dosage'), dataIndex: 'dosage', render: (value) => value || '-' },
                                            { title: t('frequency'), dataIndex: 'frequency', render: (value) => value || '-' },
                                            { title: t('duration'), dataIndex: 'duration', render: (value) => value || '-' },
                                            { title: t('notes'), dataIndex: 'notes', render: (value) => value || '-' },
                                            { title: t('diagnosis'), dataIndex: 'diagnosis', render: (value) => value || '-' }
                                        ]}
                                    />
                                )}
                            </Card>
                        )
                    },
                    // Only shown to a patient who actually has imported records,
                    // which is a minority - an empty tab on every other patient
                    // would be four sections pretending to be five.
                    ...(legacyRecords.length > 0 ? [{
                        key: 'legacy',
                        label: `${t('legacyRecords')} (${legacyRecords.length})`,
                        children: (
                            <>
            {/* What the previous system held for this patient, once somebody matched
                it to them. Kept visually apart from the visits below: these are
                imported records, not consultations recorded here. */}
            {legacyRecords.length > 0 && (
                <Card
                    title={<Space><FileTextOutlined />{t('legacyRecords')} ({legacyRecords.length})</Space>}
                    style={{ marginBottom: 16 }}
                >
                    <Collapse>
                        {legacyRecords.map((record) => (
                            <Panel
                                key={record._id}
                                header={
                                    <Space wrap>
                                        <Text strong>#{record.legacyVisitId}</Text>
                                        {record.prescriptionText && <Tag color="purple">{t('prescriptions')}</Tag>}
                                        {record.surgery?.name && <Tag color="red">{record.surgery.name}</Tag>}
                                        {record.ctScan?.findings && <Tag color="geekblue">CT</Tag>}
                                    </Space>
                                }
                            >
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    {[
                                        [t('prescriptions'), record.prescriptionText],
                                        [t('surgery'), [record.surgery?.name, record.surgery?.notes].filter(Boolean).join(' — ')],
                                        ['CT', [record.ctScan?.findings, record.ctScan?.notes].filter(Boolean).join(' — ')],
                                        [t('examination'), Object.entries(record.examination || {})
                                            .filter(([, value]) => value).map(([k, v]) => `${k}: ${v}`).join('\n')],
                                        [t('labTests'), Object.entries(record.investigation || {})
                                            .filter(([, value]) => value).map(([k, v]) => `${k}: ${v}`).join('\n')]
                                    ].filter(([, value]) => value).map(([label, value]) => (
                                        <div key={label}>
                                            <Text strong>{label}</Text>
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
                                        </div>
                                    ))}
                                </Space>
                            </Panel>
                        ))}
                    </Collapse>
                </Card>
            )}
                            </>
                        )
                    }] : [])
                ]}
            />

            <Modal
                title={`${t('createVisit')}${patient ? ` — ${patient.name}` : ''}`}
                open={visitModalVisible}
                onCancel={() => {
                    setVisitModalVisible(false);
                    setFileList([]);
                    setXrayFileList([]);
                    setCtScanFileList([]);
                    setUltrasoundFileList([]);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                confirmLoading={submitLoading}
                okButtonProps={{ disabled: submitLoading }}
                okText={t('saveVisit') || t('save')}
                // As wide as the screen sensibly allows and pinned near the top:
                // the form is laid out in columns, and the columns are what keep it
                // on one screen.
                width="min(1200px, 96vw)"
                style={{ top: 24 }}
                className="visit-modal"
                // Only the Cancel button closes the form. A click on the backdrop
                // or an Esc used to throw away a half-written consultation.
                maskClosable={false}
                keyboard={false}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateVisit}
                    onFinishFailed={handleVisitFormInvalid}
                    scrollToFirstError
                    onValuesChange={syncDerivedFields(form)}
                >
                    <VisitFormFields
                        form={form}
                        showField={showField}
                        drugs={drugs}
                        visitSuggestions={visitSuggestions}
                        showObGynVitals={showObGynVitals}
                        labCatalog={labCatalog}
                        labCategories={labCategories}
                        patientSex={patient?.gender}
                        attachmentList={fileList}
                        onAttachmentListChange={setFileList}
                        xrayFileList={xrayFileList}
                        onXrayFileListChange={setXrayFileList}
                        ctScanFileList={ctScanFileList}
                        onCtScanFileListChange={setCtScanFileList}
                        ultrasoundFileList={ultrasoundFileList}
                        onUltrasoundFileListChange={setUltrasoundFileList}
                    />
                </Form>
            </Modal>

            {/* Requesting an operation, from the patient's own page. The Surgery
                screen can do this too, but there the doctor has to find the
                patient in a dropdown first - here they are already looking at
                them, and the diagnosis from the last visit is offered filled in. */}
            <Modal
                title={`${t('addSurgery')}${patient ? ` — ${patient.name}` : ''}`}
                open={surgeryModalVisible}
                onCancel={() => {
                    setSurgeryModalVisible(false);
                    surgeryForm.resetFields();
                }}
                onOk={() => surgeryForm.submit()}
                confirmLoading={submitLoading}
                okButtonProps={{ disabled: submitLoading }}
                okText={t('save')}
                width={720}
                destroyOnClose
            >
                <Form form={surgeryForm} layout="vertical" onFinish={handleCreateSurgery}>
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="surgeryType"
                                label={t('surgeryType')}
                                rules={[{ required: true, message: t('required') }]}
                            >
                                <Input placeholder={t('surgeryType')} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="urgencyLevel"
                                label={t('urgencyLevel')}
                                rules={[{ required: true, message: t('required') }]}
                            >
                                <Select
                                    options={[
                                        { value: 'elective', label: t('elective') || 'Elective' },
                                        { value: 'urgent', label: t('urgent') || 'Urgent' },
                                        { value: 'emergency', label: t('emergency') || 'Emergency' }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="diagnosis"
                        label={t('diagnosis')}
                        rules={[{ required: true, message: t('required') }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder={t('diagnosis')} />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col xs={24} md={8}>
                            <Form.Item name="preferredDate" label={t('preferredDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="estimatedDuration" label={`${t('estimatedDuration')} (${t('minutes') || 'min'})`}>
                                <Input type="number" min={0} placeholder="90" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="anesthesiaType" label={t('anesthesiaType')}>
                                <Input placeholder={t('anesthesiaType')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col xs={24} md={8}>
                            <Form.Item name="icdCode" label={t('icdCode') || 'ICD code'}>
                                <Input placeholder={t('optional')} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item name="specialRequirements" label={t('specialRequirements')}>
                                <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('optional')} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={t('editPatient')}
                open={editPatientModalVisible}
                onCancel={() => {
                    setEditPatientModalVisible(false);
                    editPatientForm.resetFields();
                }}
                onOk={() => editPatientForm.submit()}
                confirmLoading={submitLoading}
                okButtonProps={{ disabled: submitLoading }}
                width={700}
            >
                <Form form={editPatientForm} layout="vertical" onFinish={handleUpdatePatient}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label={t('name')}
                                rules={[{ required: true, message: t('name') + ' ' + t('required') }]}
                            >
                                <Input placeholder={t('enterFullName')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phoneNumber"
                                label={t('phoneNumber')}
                                rules={[{ required: true, message: t('phoneNumber') + ' ' + t('required') }]}
                            >
                                <Input placeholder={t('enterPhoneNumber')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label={t('email')}>
                                <Input type="email" placeholder={t('email')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="dateOfBirth" label={t('dateOfBirth')}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="gender" label={t('gender')}>
                                <Select placeholder={t('select')}>
                                    <Select.Option value="male">{t('male')}</Select.Option>
                                    <Select.Option value="female">{t('female')}</Select.Option>
                                    <Select.Option value="other">{t('other')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="bloodType" label={t('bloodType')}>
                                <Select placeholder={t('select')}>
                                    <Select.Option value="A+">A+</Select.Option>
                                    <Select.Option value="A-">A-</Select.Option>
                                    <Select.Option value="B+">B+</Select.Option>
                                    <Select.Option value="B-">B-</Select.Option>
                                    <Select.Option value="AB+">AB+</Select.Option>
                                    <Select.Option value="AB-">AB-</Select.Option>
                                    <Select.Option value="O+">O+</Select.Option>
                                    <Select.Option value="O-">O-</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="occupation" label={t('occupation')}>
                        <Input placeholder={t('occupation')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="smoker" label={t('smoker')}>
                                <Select placeholder={t('select')} options={lifestyleOptions(t)} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="alcoholic" label={t('alcoholic')}>
                                <Select placeholder={t('select')} options={lifestyleOptions(t)} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="address" label={t('address')}>
                        <Input.TextArea placeholder={t('enterAddress')} rows={2} />
                    </Form.Item>

                    <Form.Item name="allergies" label={t('allergies')}>
                        <Select mode="tags" placeholder={t('allergies')} />
                    </Form.Item>

                    <Form.Item name="chronicDiseases" label={t('chronicDiseases')}>
                        <Select mode="tags" placeholder={t('chronicDiseases')} />
                    </Form.Item>

                    <Form.Item name="pastSurgicalHistory" label={t('pastSurgicalHistory')}>
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="familyHistoryFather" label={t('familyHistoryFather')}>
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="familyHistoryMother" label={t('familyHistoryMother')}>
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="notes" label={t('notes')}>
                        <Input.TextArea placeholder={t('enterNotes')} rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Visit Modal */}
            <Modal
                title={t('editVisit')}
                open={editVisitModalVisible}
                onCancel={() => {
                    setEditVisitModalVisible(false);
                    editVisitForm.resetFields();
                    setEditingVisit(null);
                    setXrayFileList([]);
                    setCtScanFileList([]);
                    setUltrasoundFileList([]);
                }}
                onOk={() => editVisitForm.submit()}
                confirmLoading={submitLoading}
                okButtonProps={{ disabled: submitLoading }}
                okText={t('save')}
                width="min(1200px, 96vw)"
                style={{ top: 24 }}
                className="visit-modal"
                // Only the Cancel button closes the form. A click on the backdrop
                // or an Esc used to throw away a half-written consultation.
                maskClosable={false}
                keyboard={false}
                destroyOnClose
            >
                <Form
                    form={editVisitForm}
                    layout="vertical"
                    onFinish={handleUpdateVisit}
                    onFinishFailed={handleVisitFormInvalid}
                    scrollToFirstError
                    onValuesChange={syncDerivedFields(editVisitForm)}
                >
                    {/* The same body as the create form, so a field this doctor has
                        switched off stays off while they are correcting a visit -
                        the edit form used to ignore the setting entirely. */}
                    <VisitFormFields
                        form={editVisitForm}
                        showField={showField}
                        drugs={drugs}
                        visitSuggestions={visitSuggestions}
                        showObGynVitals={showObGynVitals}
                        labCatalog={labCatalog}
                        labCategories={labCategories}
                        patientSex={patient?.gender}
                        xrayFileList={xrayFileList}
                        onXrayFileListChange={setXrayFileList}
                        ctScanFileList={ctScanFileList}
                        onCtScanFileListChange={setCtScanFileList}
                        ultrasoundFileList={ultrasoundFileList}
                        onUltrasoundFileListChange={setUltrasoundFileList}
                        existingXrayFiles={editingVisit?.xrayFiles || []}
                        existingCtScanFiles={editingVisit?.ctScanFiles || []}
                        existingUltrasoundFiles={editingVisit?.ultrasoundFiles || []}
                        onRemoveExistingImaging={handleRemoveExistingImaging}
                        showVisitDate
                    />
                </Form>
            </Modal>

            <LabTrendModal
                patientId={id}
                fieldId={trend?.fieldId || null}
                label={trend?.label || ''}
                onClose={() => setTrend(null)}
            />

            <PrintPrescription
                visit={selectedVisit}
                patient={patient}
                doctorData={user}
                autoPrint={Boolean(selectedVisit)}
                onAfterPrint={() => setSelectedVisit(null)}
            />
        </div >
    );
};

export default PatientDetail;
